import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { ReviewDecided, ReviewQueue, ReviewQueueSummary } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { PAGE_SIZE, useListParams } from "../lib/useListParams";
import { ListShell, SortHeader } from "../components/ListShell";
import { useCsvColumns } from "../lib/columnChoice";
import { downloadNarrowed } from "../lib/csvDownload";
import { Panel, Section } from "../components/Panel";
import { Spinner } from "../components/Spinner";
import { absent, count, orAbsent, relativeDate, shortDate } from "../lib/format";

/* Records the engine could not settle as the same or different.
 *
 * James, 2026-09-25: duplicate resolution is a core function of this app. The
 * engine settles what the evidence decides and holds the rest in
 * lake.merge_review. This is where a person settles those: both records side
 * by side, what they share, why they were paired, and the model's read, then
 * Combine, Keep separate or Not sure.
 *
 * The evidence is built in the lake by api.review_pair, so this screen shows
 * it rather than working it out. Combine queues a job that merges through the
 * engine's own merge code. Not sure hides the pair for two weeks.
 *
 * James, 2026-10-01: the engine's duplicate reviewer settles the pairs that are
 * clear before a person sees them. "Decided by EVIE" lists what it settled,
 * with its reason, and Undo puts a pair back: a pair kept apart returns to the
 * list, and a merged pair is separated by a job in the engine.
 *
 * James, 2026-10-19: the list includes what the rules settled, not only the
 * model, with a By column (engine migration 258). It is done work, kept for
 * an occasional check, so nothing on the screen counts it as waiting.
 *
 * 2026-10-03: the numbers that say whether the engine is doing its job sit
 * above the tabs, from api.review_queue_summary, one row per kind. */

const FILTERS = ["type", "unsure", "show"] as const;
/* A project has no screen of its own, so a project's "open" link goes to
   the property it is on, which api.review_side gives as property_id. */
const TYPES = [
  { key: "company", label: "Companies", route: "companies" },
  { key: "person", label: "People", route: "people" },
  { key: "property", label: "Properties", route: "properties" },
  { key: "project", label: "Projects", route: "properties" },
] as const;
type Kind = (typeof TYPES)[number]["key"];

const CSV_DEFAULT = ["id", "entity_type", "survivor_label", "loser_label", "found_by",
                     "ai_confidence", "ai_reason", "created_at"] as const;

type Side = Record<string, unknown> & { id?: string; name?: string; property_id?: string | null };
type Pair = {
  id: number; entity_type: Kind; status: string;
  rule_evidence: string[] | null; found_by: string | null;
  ai_same: boolean | null; ai_confidence: string | null; ai_reason: string | null;
  created_at: string; deferred_until: string | null;
  a: Side | null; b: Side | null; shared: string[];
};

/* The rows each type shows, in the order a person compares them. */
const FIELDS: Record<Kind, { key: string; label: string }[]> = {
  company: [
    { key: "website", label: "Website" }, { key: "hq", label: "Head office" },
    { key: "kind", label: "Type" }, { key: "buildings", label: "Properties" },
    { key: "people", label: "People" }, { key: "sources", label: "Sources" },
    { key: "newest_source", label: "Newest source" },
    { key: "sample_buildings", label: "Some properties" },
    { key: "sample_people", label: "Some people" },
  ],
  person: [
    { key: "title", label: "Title" }, { key: "employer", label: "Employer" },
    { key: "employers", label: "Current jobs" }, { key: "emails", label: "Email" },
    { key: "phones", label: "Phone" }, { key: "linkedin", label: "LinkedIn" },
    { key: "projects", label: "Projects" }, { key: "sources", label: "Sources" },
    { key: "newest_source", label: "Newest source" },
  ],
  property: [
    { key: "address", label: "Address" }, { key: "place", label: "Place" },
    { key: "type", label: "Type" }, { key: "units", label: "Units" },
    { key: "parcel", label: "Parcel" }, { key: "companies", label: "Companies" },
    { key: "projects", label: "Projects" }, { key: "sources", label: "Sources" },
    { key: "newest_source", label: "Newest source" },
  ],
  project: [
    { key: "property", label: "On the property" }, { key: "stage", label: "Stage" },
    { key: "units", label: "Units" }, { key: "kind", label: "Type" },
    { key: "market", label: "Market" }, { key: "companies", label: "Companies" },
    { key: "people", label: "People" }, { key: "dates", label: "Dates" },
    { key: "sources", label: "Sources" }, { key: "newest_source", label: "Newest source" },
  ],
};

function value(v: unknown) {
  if (v === null || v === undefined || v === "") return orAbsent(null);
  if (Array.isArray(v)) {
    return v.length ? v.map((x, i) => <div key={i}>{String(x)}</div>) : orAbsent(null);
  }
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
}

/* The link that opens a side's record: the record itself, or, for a project,
   the property it is on, when it is on one. */
function openLink(kind: Kind, route: string, side: Side | null | undefined) {
  const id = kind === "project" ? side?.property_id : side?.id;
  if (!id) return null;
  return <> <Link to={`/${route}?record=${id}`}>{kind === "project" ? "open property" : "open"}</Link></>;
}

/* The rule text when the engine stored some, or how the pair was found. */
const FOUND_BY: Record<string, string> = {
  website: "Same website", "bare name": "Short name of the other",
  name: "Same name", fuzzy: "Similar name", resolver: "Found while reading a page",
};
function whyPaired(rules: string[] | null | undefined, foundBy: string | null | undefined) {
  if (rules?.length) return rules.join(", ");
  if (foundBy) return FOUND_BY[foundBy] ?? foundBy;
  return orAbsent(null);
}

/* ai_same is null for a read of "not sure", which has a confidence of its own.
   Reading null as false printed "Different" on pairs the model could not call. */
function modelRead(p: { ai_same?: boolean | null; ai_confidence?: string | null }) {
  if (p.ai_confidence === null || p.ai_confidence === undefined) return orAbsent(null);
  const said = p.ai_same === true ? "Same" : p.ai_same === false ? "Different" : "Not sure";
  return `${said}, ${p.ai_confidence} confidence`;
}

/* How many days the oldest waiting pair has waited. "Today" rather than
   "0 days", the way relativeDate says it on the rows below. */
function days(n: number | null | undefined) {
  if (n === null || n === undefined) return absent("-", "No pairs waiting");
  if (n === 0) return "Today";
  return `${n.toLocaleString()} ${n === 1 ? "day" : "days"}`;
}

type Figure = Exclude<keyof ReviewQueueSummary, "entity_type">;

/* One row per kind: how many pairs wait for a person and how long the oldest
 * has waited, then what was settled in the last seven days and by whom. It
 * reads api.review_queue_summary (engine migrations 141 and 142), which counts
 * waiting pairs the way the list below does, with Not sure hidden, so the two
 * numbers never disagree.
 *
 * Combined by rules and by the model are the engine's own merges. Combined,
 * then undone is a merged pair somebody put back with Undo, which is a wrong
 * merge found, so it is the column to watch. The table draws with empty cells
 * until the numbers land, so the tabs under it do not move.
 *
 * It is a table rather than a strip of figures because it compares three
 * kinds on seven numbers, and that is what columns are for. */
function Summary({ token }: { token: string }) {
  const q = useQuery({
    queryKey: ["review-summary"],
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("review_queue_summary").select("*").limit(TYPES.length);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ReviewQueueSummary[];
    },
  });

  if (q.isError) {
    return (
      <p className="refusal" role="status">
        The numbers above the list could not be loaded. {(q.error as Error).message}
      </p>
    );
  }
  const byKind = new Map((q.data ?? []).map((r) => [r.entity_type, r]));
  // Blank until the row is here. A zero is a claim, and a missing row is not one.
  const cell = (r: ReviewQueueSummary | undefined, k: Figure) => (r ? count(r[k]) : "");

  return (
    <table className="tight summary">
      <caption className="sr-only">Duplicate pairs by type</caption>
      <thead>
        <tr>
          <th rowSpan={2} scope="col"><span className="sr-only">Type</span></th>
          <th colSpan={2} scope="colgroup" className="group">Waiting for a person</th>
          <th colSpan={5} scope="colgroup" className="group">Settled in the last 7 days</th>
        </tr>
        <tr>
          <th scope="col" className="num">Pairs</th>
          <th scope="col" className="num">Oldest</th>
          <th scope="col" className="num">Combined by rules</th>
          <th scope="col" className="num">Combined by the model</th>
          <th scope="col" className="num">Combined by a person</th>
          <th scope="col" className="num">Kept separate</th>
          <th scope="col" className="num">Combined, then undone</th>
        </tr>
      </thead>
      <tbody>
        {TYPES.map((t) => {
          const r = byKind.get(t.key);
          return (
            <tr key={t.key}>
              <th scope="row">{t.label}</th>
              <td className="num">{cell(r, "pending")}</td>
              <td className="num">{r ? days(r.oldest_pending_days) : ""}</td>
              <td className="num">{cell(r, "merged_by_rules_7d")}</td>
              <td className="num">{cell(r, "merged_by_model_7d")}</td>
              <td className="num">{cell(r, "merged_by_people_7d")}</td>
              <td className="num">{cell(r, "kept_7d")}</td>
              <td className="num">{cell(r, "undone_7d")}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PairPanel({ token, id, onClose }: { token: string; id: number; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [said, setSaid] = useState<string | null>(null);

  const pair = useQuery({
    queryKey: ["review-pair", id],
    queryFn: () => callFunction<Pair>(token, "review_pair", { p_id: id }),
  });

  const decide = useMutation({
    mutationFn: (decision: "combine" | "keep" | "unsure") =>
      callFunction<{ status: string }>(token, "decide_review", { p_id: id, p_decision: decision }),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ["review-queue"] });
      queryClient.invalidateQueries({ queryKey: ["review-summary"] });
      setSaid(r?.status === "merging" ? "Combining." : r?.status === "kept" ? "Kept separate." : "Marked not sure.");
      window.setTimeout(onClose, 700);
    },
    onError: (e: Error) => setSaid(e.message),
  });

  const p = pair.data;
  const kind = p?.entity_type;
  const route = TYPES.find((t) => t.key === kind)?.route ?? "companies";

  return (
    <Panel title={p ? `${p.a?.name ?? "Record A"} and ${p.b?.name ?? "Record B"}` : "Loading"}
           subtitle={p ? `Pair #${p.id}` : undefined} onClose={onClose}>
      {pair.isLoading ? <p className="muted"><Spinner />Loading</p>
        : pair.isError ? <p className="refusal">{(pair.error as Error).message}</p>
        : !p || !kind ? <p className="refusal">No pair with that number.</p>
        : (
          <>
            <table className="tight">
              <thead>
                <tr>
                  <th />
                  <th>A{openLink(kind, route, p.a)}</th>
                  <th>B{openLink(kind, route, p.b)}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="muted">Name</td>
                  <td><strong>{orAbsent(p.a?.name)}</strong></td>
                  <td><strong>{orAbsent(p.b?.name)}</strong></td>
                </tr>
                {FIELDS[kind].map((f) => (
                  <tr key={f.key}>
                    <td className="muted">{f.label}</td>
                    <td>{f.key === "newest_source" ? shortDate(p.a?.[f.key] as string | null) : value(p.a?.[f.key])}</td>
                    <td>{f.key === "newest_source" ? shortDate(p.b?.[f.key] as string | null) : value(p.b?.[f.key])}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <Section title="In common">
              {p.shared?.length
                ? <ul>{p.shared.map((s, i) => <li key={i}>{s}</li>)}</ul>
                : <p className="muted">Only the name matches.</p>}
            </Section>

            <Section title="Why these were paired">
              <p>{whyPaired(p.rule_evidence, p.found_by)}</p>
              <p className="muted">Model: {modelRead(p)}{p.ai_reason ? `. ${p.ai_reason}` : ""}</p>
              {p.deferred_until ? <p className="muted">Marked not sure until {shortDate(p.deferred_until)}.</p> : null}
            </Section>

            <div className="bar">
              <button onClick={() => decide.mutate("combine")} disabled={decide.isPending}>Combine</button>
              <button onClick={() => decide.mutate("keep")} disabled={decide.isPending}>Keep separate</button>
              <button onClick={() => decide.mutate("unsure")} disabled={decide.isPending}>Not sure</button>
              {decide.isPending ? <Spinner /> : null}
              {said ? <span className={decide.isError ? "refusal" : "muted"} role="status">{said}</span> : null}
            </div>
            <p className="hint">Combine keeps A and folds B into it. Not sure hides the pair for two weeks.</p>
          </>
        )}
    </Panel>
  );
}

const DECIDED_CSV_DEFAULT = ["id", "entity_type", "survivor_label", "loser_label", "status",
                             "decided_by_label", "decision_reason", "decided_at"] as const;

function UndoButton({ token, row }: { token: string; row: ReviewDecided }) {
  const queryClient = useQueryClient();
  const undo = useMutation({
    mutationFn: () => callFunction<{ status: string }>(token, "undo_review", { p_id: row.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["review-decided"] });
      queryClient.invalidateQueries({ queryKey: ["review-queue"] });
      queryClient.invalidateQueries({ queryKey: ["review-summary"] });
    },
  });
  if (row.undoing) return <span className="muted">Separating</span>;
  if (undo.isSuccess) {
    return <span className="muted" role="status">{undo.data?.status === "undoing" ? "Separating" : "Back on the list"}</span>;
  }
  return (
    <>
      <button onClick={() => undo.mutate()} disabled={undo.isPending}>Undo</button>
      {undo.isPending ? <Spinner /> : null}
      {undo.isError ? <span className="refusal" role="alert">{(undo.error as Error).message}</span> : null}
    </>
  );
}

function DecidedList({ session, params, kind, aboveList }: {
  session: SignedIn; params: ReturnType<typeof useListParams>; kind: Kind; aboveList: ReactNode;
}) {
  const csvColumns = useCsvColumns("duplicates-decided", "review_decided", DECIDED_CSV_DEFAULT);
  const queryClient = useQueryClient();
  // The screen's sort is checked against review_queue's columns. A name sort
  // carries over, and anything else reads newest decision first.
  const byName = params.sort === "survivor_label" || params.sort === "loser_label";
  const sort = byName ? params.sort : "decided_at";
  const ascending = byName ? params.ascending : false;

  const narrowedFor = (columns: string) => {
    let q = apiClient(session.idToken).from("review_decided")
      .select(columns, { count: "exact" }).eq("entity_type", kind);
    // The characters or() reads as its own syntax stand as the wildcard, so
    // "Partners, LLC" finds the name with its comma. It made the request fail
    // until 2026-10-20.
    const s = params.search.trim().replace(/[(),*"\\]/g, "*");
    if (s) q = q.or(`survivor_label.ilike.*${s}*,loser_label.ilike.*${s}*`);
    return q;
  };

  const query = useQuery({
    queryKey: ["review-decided", kind, params.page, sort, ascending, params.search],
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error, count } = await narrowedFor(
        "id,entity_type,survivor_id,loser_id,survivor_label,loser_label,status,decided_by_label,decision_reason,decided_at,undoing")
        .order(sort, { ascending, nullsFirst: false })
        .order("id", { ascending: false })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return { rows: (data ?? []) as ReviewDecided[], total: count ?? null };
    },
  });
  const rows = query.data?.rows ?? [];
  const route = TYPES.find((t) => t.key === kind)?.route ?? "companies";

  return (
    <ListShell
      title="Duplicates"
      view="review_decided"
      searchLabel="Name"
      params={params}
      csvColumns={csvColumns}
      total={query.data?.total ?? null}
      rowCount={rows.length}
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      isError={query.isError}
      error={query.error as Error | null}
      dataUpdatedAt={query.dataUpdatedAt}
      onRefresh={() => {
        queryClient.invalidateQueries({ queryKey: ["review-decided"] });
        queryClient.invalidateQueries({ queryKey: ["review-summary"] });
      }}
      onRetry={() => query.refetch()}
      onDownload={() =>
        downloadNarrowed({
          name: "duplicates-decided-by-evie",
          columns: csvColumns.chosen,
          total: query.data?.total ?? null,
          fetchAll: async (ceiling) => {
            const { data, error } = await narrowedFor(csvColumns.chosen.join(","))
              .order(sort, { ascending, nullsFirst: false })
              .range(0, ceiling - 1);
            if (error) throw new Error(apiErrorMessage(error));
            return (data ?? []) as unknown as ReviewDecided[];
          },
        })
      }
      emptyEverything="No pairs decided by EVIE yet."
      beforeList={aboveList}
    >
      <table>
        <thead>
          <tr>
            <SortHeader column="survivor_label" label="A" params={params} />
            <SortHeader column="loser_label" label="B" params={params} />
            <th>Decision</th>
            <th>By</th>
            <th>Reason</th>
            <th>Decided</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.survivor_id ? <Link to={`/${route}?record=${r.survivor_id}`}>{orAbsent(r.survivor_label)}</Link>
                                 : orAbsent(r.survivor_label)}</td>
              <td>{r.status === "kept" && r.loser_id
                    ? <Link to={`/${route}?record=${r.loser_id}`}>{orAbsent(r.loser_label)}</Link>
                    : orAbsent(r.loser_label)}</td>
              <td>{r.status === "merged" ? "Combined" : "Kept separate"}</td>
              <td>{orAbsent(r.decided_by_label)}</td>
              <td className="muted">{orAbsent(r.decision_reason)}</td>
              <td className="muted">{relativeDate(r.decided_at)}</td>
              <td><UndoButton token={session.idToken} row={r} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </ListShell>
  );
}

export function Duplicates({ session }: { session: SignedIn }) {
  /* Likely matches first: the model's "same" reads, high then medium then low,
     from engine migration 118. Oldest first within each. */
  const params = useListParams("likely_same", FILTERS, false, "review_queue");
  const csvColumns = useCsvColumns("duplicates", "review_queue", CSV_DEFAULT);
  const queryClient = useQueryClient();
  const kind = (TYPES.find((t) => t.key === params.filters["type"])?.key ?? "company") as Kind;
  const showUnsure = params.filters["unsure"] === "shown";
  const showDecided = params.filters["show"] === "decided";

  const narrowedFor = (columns: string) => {
    let q = apiClient(session.idToken).from("review_queue")
      .select(columns, { count: "exact" }).eq("entity_type", kind);
    if (!showUnsure) q = q.eq("deferred", false);
    // The characters or() reads as its own syntax stand as the wildcard, so
    // "Partners, LLC" finds the name with its comma. It made the request fail
    // until 2026-10-20.
    const s = params.search.trim().replace(/[(),*"\\]/g, "*");
    if (s) q = q.or(`survivor_label.ilike.*${s}*,loser_label.ilike.*${s}*`);
    return q;
  };

  const LIST_COLUMNS = "id,entity_type,survivor_label,loser_label,found_by,rule_evidence," +
                       "ai_same,ai_confidence,likely_same,created_at,deferred,merging";

  const query = useQuery({
    enabled: !showDecided,
    queryKey: ["review-queue", kind, showUnsure, params.page, params.sort, params.ascending, params.search],
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error, count } = await narrowedFor(LIST_COLUMNS)
        // Not sure goes to the bottom when shown, whatever the sort.
        .order("deferred", { ascending: true })
        .order(params.sort, { ascending: params.ascending, nullsFirst: false })
        .order("created_at", { ascending: true })
        .order("id", { ascending: true })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return { rows: (data ?? []) as ReviewQueue[], total: count ?? null };
    },
  });

  const rows = query.data?.rows ?? [];
  const open = params.openRecord ? Number(params.openRecord) : null;

  /* The summary, then the tabs. The tabs name no counts: the table right above
     them has each kind's waiting pairs in its first column, and the same number
     in two places is one place too many. */
  const aboveList = (
    <>
      <Summary token={session.idToken} />
      <div className="bar" role="tablist" aria-label="Which pairs">
        {([["", "Waiting for a person"], ["decided", "Decided by EVIE"]] as const).map(([key, label]) => (
          <button key={key} role="tab" aria-selected={(params.filters["show"] ?? "") === key}
                  className={(params.filters["show"] ?? "") === key ? "pressed" : undefined}
                  onClick={() => params.set({ page: 1, filters: { ...params.filters, show: key } })}>
            {label}
          </button>
        ))}
      </div>
      <div className="bar" role="tablist" aria-label="Type of record">
        {TYPES.map((t) => (
          <button key={t.key} role="tab" aria-selected={kind === t.key}
                  className={kind === t.key ? "pressed" : undefined}
                  onClick={() => params.set({ page: 1, filters: { ...params.filters, type: t.key } })}>
            {t.label}
          </button>
        ))}
      </div>
    </>
  );

  if (showDecided) {
    return <DecidedList session={session} params={params} kind={kind} aboveList={aboveList} />;
  }

  return (
    <>
      <ListShell
        title="Duplicates"
        view="review_queue"
        searchLabel="Name"
        params={params}
        csvColumns={csvColumns}
        total={query.data?.total ?? null}
        rowCount={rows.length}
        isLoading={query.isLoading}
        isFetching={query.isFetching}
        isError={query.isError}
        error={query.error as Error | null}
        dataUpdatedAt={query.dataUpdatedAt}
        onRefresh={() => {
          queryClient.invalidateQueries({ queryKey: ["review-queue"] });
          queryClient.invalidateQueries({ queryKey: ["review-summary"] });
        }}
        onRetry={() => query.refetch()}
        onDownload={() =>
          downloadNarrowed({
            name: "duplicates",
            columns: csvColumns.chosen,
            total: query.data?.total ?? null,
            fetchAll: async (ceiling) => {
              const { data, error } = await narrowedFor(csvColumns.chosen.join(","))
                .order(params.sort, { ascending: params.ascending, nullsFirst: false })
                .range(0, ceiling - 1);
              if (error) throw new Error(apiErrorMessage(error));
              return (data ?? []) as unknown as ReviewQueue[];
            },
          })
        }
        emptyEverything={showUnsure ? "No pairs waiting." : "No pairs waiting. Pairs marked not sure are hidden."}
        beforeList={aboveList}
        filters={
          <>
            <label htmlFor="f-unsure">Not sure</label>
            <select id="f-unsure" value={params.filters["unsure"] ?? ""}
                    onChange={(e) => params.set({ page: 1, filters: { ...params.filters, unsure: e.target.value } })}>
              <option value="">Hidden</option>
              <option value="shown">Shown at the bottom</option>
            </select>
          </>
        }
      >
        <table>
          <thead>
            <tr>
              <SortHeader column="survivor_label" label="A" params={params} />
              <SortHeader column="loser_label" label="B" params={params} />
              <th>Why paired</th>
              <SortHeader column="likely_same" label="Model" params={params} />
              <SortHeader column="created_at" label="Waiting since" params={params} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={r.deferred ? "muted" : undefined}>
                <td>
                  <button className="link" onClick={() => params.set({ openRecord: String(r.id) })}>
                    {orAbsent(r.survivor_label)}
                  </button>
                </td>
                <td>{orAbsent(r.loser_label)}</td>
                <td className="muted">{whyPaired(r.rule_evidence, r.found_by)}</td>
                <td className="muted">
                  {r.merging ? "Combining" : r.deferred ? "Not sure" : modelRead(r)}
                </td>
                <td className="muted">{relativeDate(r.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ListShell>
      {open ? (
        <PairPanel token={session.idToken} id={open} onClose={() => params.set({ openRecord: null })} />
      ) : null}
    </>
  );
}
